import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Trash2, UserPlus, Copy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLES = ["pending", "admin", "editor", "smm", "client"];

function randomPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#";
  let p = "";
  for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
}

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "editor", temp_password: randomPassword(), client_id: "" });
  const [createdInfo, setCreatedInfo] = useState(null);

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
    setUsers(u.data);
    setClients(c.data);
  };

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email || !form.name) { toast.error("Email & name required"); return; }
    try {
      const r = await api.post("/users/invite", {
        email: form.email,
        name: form.name,
        role: form.role,
        temp_password: form.temp_password,
        client_id: form.role === "client" ? (form.client_id || null) : null,
      });
      setCreatedInfo({ email: r.data.email, password: form.temp_password });
      setOpen(false);
      setForm({ email: "", name: "", role: "editor", temp_password: randomPassword(), client_id: "" });
      load();
      toast.success("User created. Share credentials below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copyCreds = () => {
    if (!createdInfo) return;
    const txt = `Doxico login\nEmail: ${createdInfo.email}\nTemp password: ${createdInfo.password}\n(You'll be asked to set a new password on first sign in.)`;
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const updateRole = async (u, role, client_id = null) => {
    try {
      const r = await api.patch(`/users/${u.user_id}/role`, { role, client_id });
      setUsers((p) => p.map((x) => x.user_id === u.user_id ? r.data : x));
      toast.success("Role updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      setUsers((p) => p.filter((x) => x.user_id !== u.user_id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="team-page">
      <PageHeader
        eyebrow="Permissions"
        title="Team & Roles"
        description="Create accounts with email + temporary password. Users set their own password on first sign in."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-user-btn" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                <span className="uppercase tracking-[0.15em] text-xs">Invite User</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border border-neutral-800 rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Invite Teammate</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Full Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="invite-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="invite-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800" data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                      {["editor", "smm", "admin", "client"].map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "client" && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Linked Client</Label>
                    <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-neutral-900 border-neutral-800"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        <SelectItem value="none">—</SelectItem>
                        {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Temporary Password</Label>
                  <div className="flex gap-2 mt-2">
                    <Input value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="rounded-none bg-neutral-900 border-neutral-800 font-mono text-xs" data-testid="invite-password" />
                    <Button type="button" onClick={() => setForm({ ...form, temp_password: randomPassword() })} variant="ghost" className="rounded-none border border-neutral-800 hover:bg-neutral-900 h-9 px-3 text-xs uppercase tracking-wider">Regen</Button>
                  </div>
                  <p className="text-[10px] text-neutral-600 mt-1.5">User will be forced to change it on first sign in.</p>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button onClick={invite} data-testid="invite-submit" className="rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">Create User</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 lg:px-10 py-8 space-y-6">
        {createdInfo && (
          <div className="border border-[#00C97A]/40 bg-[#00C97A]/5 px-5 py-4 flex flex-wrap items-center gap-3" data-testid="created-creds">
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00C97A] mb-1">User created — share these once</div>
              <div className="font-mono text-neutral-200">{createdInfo.email}</div>
              <div className="font-mono text-neutral-200">Password: {createdInfo.password}</div>
            </div>
            <Button onClick={copyCreds} className="rounded-none bg-neutral-900 border border-neutral-800 hover:bg-neutral-800 ml-auto text-xs h-8">
              <Copy className="w-3 h-3 mr-2" /> Copy
            </Button>
            <button onClick={() => setCreatedInfo(null)} className="text-neutral-500 hover:text-white text-xs">Dismiss</button>
          </div>
        )}

        <div className="border border-neutral-900 overflow-x-auto">
          <table className="w-full" data-testid="team-table">
            <thead>
              <tr className="border-b border-neutral-900 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Member</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-left px-4 py-3 font-medium">Linked Client</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-neutral-900" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-neutral-800 flex items-center justify-center text-xs font-semibold">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="text-sm font-medium">{u.name}{u.user_id === me?.user_id && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#00C97A]">(you)</span>}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-neutral-400">{u.email}</td>
                  <td className="px-4 py-4">
                    <Select value={u.role} onValueChange={(v) => updateRole(u, v, u.linked_client_id)}>
                      <SelectTrigger className="rounded-none bg-neutral-900 border-neutral-800 w-32 h-8 text-xs uppercase tracking-wider" data-testid={`role-select-${u.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-4">
                    {u.role === "client" ? (
                      <Select value={u.linked_client_id || "none"} onValueChange={(v) => updateRole(u, "client", v === "none" ? null : v)}>
                        <SelectTrigger className="rounded-none bg-neutral-900 border-neutral-800 w-40 h-8 text-xs" data-testid={`client-link-${u.user_id}`}>
                          <SelectValue placeholder="Link client" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-neutral-950 border-neutral-800">
                          <SelectItem value="none">—</SelectItem>
                          {clients.map((c) => (
                            <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-neutral-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    {u.user_id !== me?.user_id && (
                      <button onClick={() => remove(u)} className="text-neutral-500 hover:text-red-400 transition-colors" data-testid={`delete-user-${u.user_id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
